import React, { useEffect, useState } from 'react';
import { HomeIcon, CommunityIcon, ChatIcon, CalendarIcon, TrendIcon } from '../icons';
import { isPhoneViewport } from '../../utils/mobileViewport';

/**
 * BottomNavigation — SuperKonnected mobile bottom bar
 *
 * Matches HTML reference design:
 *   - 58px height  (--nav-h)
 *   - Active icon gets a brand-light pill background (28×28px, radius 8)
 *   - Active label: brand green #2d6b5e
 *   - Inbox tab shows red badge dot when there are unread messages
 *   - 5 tabs: Home / Netwerk / Inbox / Agenda / Vibes
 *
 * Layout uses pure inline styles to avoid Tailwind JIT / safe-area issues.
 */

const BRAND        = '#2d6b5e';
const BRAND_LIGHT  = '#e6f2ef';
const INACTIVE_CLR = '#9a9890';
const ICON_SIZE    = 20;

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  iconActive: React.ReactNode;
  hash: string;
  matchHashes: string[];
}

interface BottomNavigationProps {
  /** Fade bottom nav while scrolling feed content */
  dimmed?: boolean;
}

const BottomNavigation: React.FC<BottomNavigationProps> = ({ dimmed = false }) => {
  const [activeTab, setActiveTab] = useState<string>('home');
  const [isMobile, setIsMobile] = useState(false);

  const navItems: NavItem[] = [
    {
      id: 'home',
      label: 'Home',
      icon:       <HomeIcon size={ICON_SIZE} weight="regular" color={INACTIVE_CLR} />,
      iconActive: <HomeIcon size={ICON_SIZE} weight="fill"    color={BRAND}        />,
      hash: '#dashboard',
      matchHashes: ['dashboard', ''],
    },
    {
      id: 'netwerk',
      label: 'Netwerk',
      icon:       <CommunityIcon size={ICON_SIZE} weight="regular" color={INACTIVE_CLR} />,
      iconActive: <CommunityIcon size={ICON_SIZE} weight="fill"    color={BRAND}        />,
      hash: '#connecties',
      matchHashes: ['connecties'],
    },
    {
      id: 'inbox',
      label: 'Inbox',
      icon:       <ChatIcon size={ICON_SIZE} weight="regular" color={INACTIVE_CLR} />,
      iconActive: <ChatIcon size={ICON_SIZE} weight="fill"    color={BRAND}        />,
      hash: '#inbox',
      matchHashes: ['inbox', 'chat'],
    },
    {
      id: 'agenda',
      label: 'Agenda',
      icon:       <CalendarIcon size={ICON_SIZE} weight="regular" color={INACTIVE_CLR} />,
      iconActive: <CalendarIcon size={ICON_SIZE} weight="fill"    color={BRAND}        />,
      hash: '#agenda',
      matchHashes: ['agenda'],
    },
    {
      id: 'vibes',
      label: 'Vibes',
      icon:       <TrendIcon size={ICON_SIZE} weight="regular" color={INACTIVE_CLR} />,
      iconActive: <TrendIcon size={ICON_SIZE} weight="fill"    color={BRAND}        />,
      hash: '#vibes',
      matchHashes: ['vibes'],
    },
  ];

  const getActiveFromHash = (): string => {
    const hash = window.location.hash.replace('#', '').split('/')[0];
    for (const item of navItems) {
      if (item.matchHashes.includes(hash)) return item.id;
    }
    return 'home';
  };

  useEffect(() => {
    const update = () => setActiveTab(getActiveFromHash());
    update();
    window.addEventListener('hashchange', update);
    return () => window.removeEventListener('hashchange', update);
  }, []);

  useEffect(() => {
    const check = () => setIsMobile(isPhoneViewport());
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  if (!isMobile) return null;

  const handleNavClick = (item: NavItem) => {
    window.location.hash = item.hash;
    setActiveTab(item.id);
  };

  return (
    <nav
      aria-label="Hoofdnavigatie"
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: '58px',
        backgroundColor: '#ffffff',
        borderTop: '0.5px solid rgba(0,0,0,0.07)',
        zIndex: 9990,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        paddingTop: '4px',
        boxShadow: '0 -1px 8px rgba(0,0,0,0.05)',
        WebkitTapHighlightColor: 'transparent',
        opacity: dimmed ? 0.15 : 1,
        transition: 'opacity 300ms ease-in-out',
      } as React.CSSProperties}
    >
      {navItems.map((item) => {
        const isActive = activeTab === item.id;
        const isInbox  = item.id === 'inbox';

        return (
          <button
            key={item.id}
            onClick={() => handleNavClick(item)}
            aria-label={item.label}
            aria-current={isActive ? 'page' : undefined}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '3px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              position: 'relative',
              padding: '5px 0',
              WebkitTapHighlightColor: 'transparent',
              outline: 'none',
            } as React.CSSProperties}
          >
            {/* Icon container — gets brand-light pill when active */}
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                borderRadius: 8,
                background: isActive ? BRAND_LIGHT : 'transparent',
                transition: 'background 0.13s',
                lineHeight: 1,
                position: 'relative',
              }}
            >
              {isActive ? item.iconActive : item.icon}

              {/* Inbox unread badge dot */}
              {isInbox && (
                <span
                  id="bottom-nav-inbox-badge"
                  style={{
                    display: 'none',
                    position: 'absolute',
                    top: 3,
                    right: 'calc(50% - 20px)',
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: '#e74c3c',
                    border: '1.5px solid #ffffff',
                  }}
                />
              )}
            </span>

            {/* Label */}
            <span
              style={{
                fontSize: '10px',
                fontWeight: isActive ? 500 : 400,
                color: isActive ? BRAND : INACTIVE_CLR,
                letterSpacing: '0.01em',
                lineHeight: 1,
                transition: 'color 0.13s, font-weight 0.13s',
                fontFamily: 'inherit',
              }}
            >
              {item.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
};

export default BottomNavigation;
